import React, {useEffect, useRef, useState, useCallback} from 'react';
import * as THREE from 'three';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {CabinetMesh} from '../types';
import AddCabinetPopup from './FloatingMenu/AddCabinetPopup';
import {
    findNearestCabinetOnSide,
    getClampingDistanceForSide,
    SideButtonGroups,
    createButtonSprite,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {CAMERA_ROTATING_ATTRIBUTE} from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';

interface AddCabinetButton3DProps {
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    renderer: React.MutableRefObject<THREE.WebGLRenderer>;
    sideButtonGroupsRef: React.MutableRefObject<SideButtonGroups | null>;
    onButtonClick?: (side: 'left' | 'right', cabinet: THREE.Group) => void;
    onAddFromCatalog?: (side: 'left' | 'right', cabinet: THREE.Group) => void;
    onAddFromExisting?: (side: 'left' | 'right', cabinet: THREE.Group) => void;
}

const AddCabinetButton3D = ({
    scene,
    camera,
    renderer,
    sideButtonGroupsRef,
    onButtonClick,
    onAddFromCatalog,
    onAddFromExisting,
}: AddCabinetButton3DProps): JSX.Element | null => {
    const {selectedCabinetGroup, cabinetGroupListRef} = useRoomPlannerContext();
    const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
    const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
    const hoveredButtonRef = useRef<'left' | 'right' | null>(null);

    // Create persistent refs that always point to current buttons
    const leftButtonRef = useRef<THREE.Sprite | null>(null);
    const rightButtonRef = useRef<THREE.Sprite | null>(null);
    const buttonGroupRef = useRef<THREE.Group | null>(null);

    // Popup state
    const [showPopup, setShowPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});
    const [popupSide, setPopupSide] = useState<'left' | 'right'>('left');
    const [popupDistance, setPopupDistance] = useState<number | null>(null);

    // Helper: Get wall group
    const getWallGroup = useCallback(() => {
        return scene.children.find(
            (ch) =>
                ch.name === ObjectNameMap.rectangularWall ||
                ch.name === ObjectNameMap.lShapeWall
        ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;
    }, [scene]);

    // Mouse hover handling
    useEffect(() => {
        if (!renderer.current || !camera) return;

        // Leaves the grab cursor alone while the camera is being orbited.
        const setCursor = (cursor: string) => {
            const domElement = renderer.current?.domElement;
            if (!domElement?.hasAttribute(CAMERA_ROTATING_ATTRIBUTE)) {
                if (domElement) domElement.style.cursor = cursor;
            }
        };

        const handleMouseMove = (event: MouseEvent) => {
            const sideButtonGroups = sideButtonGroupsRef.current;
            if (
                !selectedCabinetGroup ||
                !sideButtonGroups ||
                !sideButtonGroups.buttonGroup.visible ||
                (!sideButtonGroups.leftButton.visible &&
                    !sideButtonGroups.rightButton.visible)
            ) {
                setCursor('default');
                return;
            }

            // Update refs to current buttons
            leftButtonRef.current = sideButtonGroups.leftButton;
            rightButtonRef.current = sideButtonGroups.rightButton;
            buttonGroupRef.current = sideButtonGroups.buttonGroup;

            const rect = renderer.current.domElement.getBoundingClientRect();
            mouseRef.current.x =
                ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouseRef.current.y =
                -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycasterRef.current.setFromCamera(mouseRef.current, camera);

            const buttonsToCheck = [
                sideButtonGroups.leftButton,
                sideButtonGroups.rightButton,
            ].filter((btn) => btn.visible);
            const intersects = raycasterRef.current.intersectObjects(
                buttonsToCheck,
                false
            );

            let newHovered: 'left' | 'right' | null = null;

            if (intersects.length > 0) {
                const closest = intersects.reduce((a, b) =>
                    a.distance < b.distance ? a : b
                );
                const buttonObject = closest.object as THREE.Sprite & {
                    userData: {side: 'left' | 'right'};
                };
                newHovered = buttonObject.userData?.side || null;
                setCursor('pointer');
            } else {
                setCursor('default');
            }

            if (newHovered !== hoveredButtonRef.current) {
                hoveredButtonRef.current = newHovered;

                // Update button sprites on hover
                ['left', 'right'].forEach((side) => {
                    const button =
                        side === 'left'
                            ? sideButtonGroups.leftButton
                            : sideButtonGroups.rightButton;
                    const isHovered = newHovered === side;
                    const currentOpacity = button.material.opacity;
                    const opacity = isHovered ? 1.0 : currentOpacity;

                    // Only update if hover state changed
                    if (isHovered || newHovered === null) {
                        const newSprite = createButtonSprite(
                            isHovered,
                            opacity
                        );
                        newSprite.position.copy(button.position);
                        newSprite.name = `${side}AddButton`;
                        newSprite.userData.side = side;
                        newSprite.visible = button.visible;
                        newSprite.updateMatrixWorld(true);

                        const oldMaterial = button.material;
                        const oldTexture = oldMaterial.map;

                        sideButtonGroups.buttonGroup.remove(button);
                        sideButtonGroups.buttonGroup.add(newSprite);
                        sideButtonGroups.buttonGroup.updateMatrixWorld(true);

                        if (side === 'left') {
                            sideButtonGroups.leftButton = newSprite;
                            leftButtonRef.current = newSprite;
                        } else {
                            sideButtonGroups.rightButton = newSprite;
                            rightButtonRef.current = newSprite;
                        }

                        oldTexture?.dispose();
                        oldMaterial.dispose();
                    }
                });
            }
        };

        renderer.current.domElement.addEventListener(
            'mousemove',
            handleMouseMove
        );

        return () => {
            renderer.current?.domElement.removeEventListener(
                'mousemove',
                handleMouseMove
            );
            if (renderer.current) {
                renderer.current.domElement.style.cursor = 'default';
            }
        };
    }, [renderer, camera, selectedCabinetGroup, sideButtonGroupsRef]);

    // Register with ProductEvents for click handling
    // This effect creates the handler function
    const handleButtonClick = useCallback(
        (
            side: 'left' | 'right',
            cabinet: THREE.Group,
            position?: {x: number; y: number}
        ) => {
            const cabinetMesh = cabinet as CabinetMesh;
            const actualSide = side;

            const allCabinets = cabinetGroupListRef?.current || [];
            const nearest = findNearestCabinetOnSide(
                cabinet,
                actualSide,
                allCabinets
            );
            const snappingDistance = nearest ? nearest.distance : null;

            const wallGroup = getWallGroup();
            let clampingDistance: number | null = null;

            if (wallGroup) {
                const roomType =
                    wallGroup.name === ObjectNameMap.lShapeWall
                        ? 'LSHAPED'
                        : 'RECTANGULAR';
                clampingDistance = getClampingDistanceForSide(
                    cabinetMesh,
                    actualSide,
                    allCabinets,
                    wallGroup,
                    camera,
                    roomType
                );
            }

            const distance =
                clampingDistance !== null &&
                (snappingDistance === null ||
                    clampingDistance < snappingDistance)
                    ? clampingDistance
                    : snappingDistance;

            const popupPos = position || {
                x:
                    renderer.current.domElement.getBoundingClientRect().left +
                    renderer.current.domElement.getBoundingClientRect().width /
                        2,
                y:
                    renderer.current.domElement.getBoundingClientRect().top +
                    renderer.current.domElement.getBoundingClientRect().height /
                        2,
            };

            setPopupSide(actualSide);
            setPopupPosition(popupPos);
            setPopupDistance(distance);
            setShowPopup(true);

            onButtonClick?.(actualSide, cabinet);
        },
        [cabinetGroupListRef, getWallGroup, camera, renderer, onButtonClick]
    );

    // Register handlers whenever buttons become available
    // This effect runs whenever selectedCabinetGroup changes or buttons are created
    useEffect(() => {
        if (!renderer.current || !camera || !selectedCabinetGroup) {
            // Clear handlers when no cabinet is selected
            productEvents.setSideButtonHandlers(
                {
                    leftButton: {current: null},
                    rightButton: {current: null},
                    buttonGroup: {current: null},
                },
                {onButtonClick: undefined}
            );
            return;
        }

        const sideButtonGroups = sideButtonGroupsRef.current;
        if (sideButtonGroups) {
            // Update refs to point to current buttons
            leftButtonRef.current = sideButtonGroups.leftButton;
            rightButtonRef.current = sideButtonGroups.rightButton;
            buttonGroupRef.current = sideButtonGroups.buttonGroup;

            productEvents.setSideButtonHandlers(
                {
                    leftButton: leftButtonRef,
                    rightButton: rightButtonRef,
                    buttonGroup: buttonGroupRef,
                },
                {onButtonClick: handleButtonClick}
            );
        }
    }, [
        renderer,
        camera,
        selectedCabinetGroup,
        handleButtonClick,
        sideButtonGroupsRef,
    ]);

    // Additional effect to handle case when buttons are created after selection
    // This uses a polling mechanism to check if buttons become available
    useEffect(() => {
        if (!renderer.current || !camera || !selectedCabinetGroup) return;

        // Check if buttons are available, and if not, set up a check
        const checkAndRegister = () => {
            const sideButtonGroups = sideButtonGroupsRef.current;
            if (
                sideButtonGroups &&
                sideButtonGroups.leftButton &&
                sideButtonGroups.rightButton
            ) {
                // Buttons are available, ensure handlers are registered
                leftButtonRef.current = sideButtonGroups.leftButton;
                rightButtonRef.current = sideButtonGroups.rightButton;
                buttonGroupRef.current = sideButtonGroups.buttonGroup;

                productEvents.setSideButtonHandlers(
                    {
                        leftButton: leftButtonRef,
                        rightButton: rightButtonRef,
                        buttonGroup: buttonGroupRef,
                    },
                    {onButtonClick: handleButtonClick}
                );
                return true; // Buttons are ready
            }
            return false; // Buttons not ready yet
        };

        // Check immediately
        if (checkAndRegister()) {
            return; // Already registered
        }

        // If buttons aren't ready, poll for a short time
        const maxAttempts = 10;
        let attempts = 0;
        const intervalId = setInterval(() => {
            attempts++;
            if (checkAndRegister() || attempts >= maxAttempts) {
                clearInterval(intervalId);
            }
        }, 50); // Check every 50ms

        return () => {
            clearInterval(intervalId);
        };
    }, [
        renderer,
        camera,
        selectedCabinetGroup,
        handleButtonClick,
        sideButtonGroupsRef,
    ]);

    // Close popup when cabinet deselected
    useEffect(() => {
        if (!selectedCabinetGroup) {
            setShowPopup(false);
        }
    }, [selectedCabinetGroup]);

    const handleClosePopup = useCallback(() => setShowPopup(false), []);

    const handleAddFromCatalog = useCallback(() => {
        if (selectedCabinetGroup) {
            onAddFromCatalog?.(popupSide, selectedCabinetGroup);
        }
    }, [selectedCabinetGroup, popupSide, onAddFromCatalog]);

    const handleAddFromExisting = useCallback(() => {
        if (selectedCabinetGroup) {
            onAddFromExisting?.(popupSide, selectedCabinetGroup);
        }
    }, [selectedCabinetGroup, popupSide, onAddFromExisting]);

    if (!showPopup) return null;

    return (
        <AddCabinetPopup
            position={popupPosition}
            side={popupSide}
            positioningSide={popupSide}
            distance={popupDistance}
            onClose={handleClosePopup}
            onAddFromCatalog={handleAddFromCatalog}
            onAddFromExisting={handleAddFromExisting}
        />
    );
};

export default AddCabinetButton3D;
